import type { ComponentProps } from 'react';
import {
  Briefcase,
  Hammer,
  Users,
  Zap,
  CheckCircle,
  FileArchive,
  Image,
  UserCheck,
  TrendingUp,
} from 'lucide-react';
import type { BuildDashboardStats } from '../../types/buildTeam';
import { StatTile } from '../ui/StatTile';

type StatTone = ComponentProps<typeof StatTile>['tone'];

interface BuildStatsCardsProps {
  stats: BuildDashboardStats | null;
  /** Render shimmering placeholder tiles while the first stats load. */
  loading?: boolean;
}

const GRID = 'grid grid-cols-2 md:grid-cols-4 gap-3';

export const BuildStatsCards = ({ stats, loading = false }: BuildStatsCardsProps) => {
  if (!stats) {
    if (!loading) return null;
    return (
      <div className={GRID}>
        {Array.from({ length: 8 }).map((_, i) => (
          <StatTile key={i} label="" value="" loading />
        ))}
      </div>
    );
  }

  const projectsInProgress = stats.projects_by_status?.in_progress ?? 0;
  const buildsInProgress = stats.builds_by_status?.in_progress ?? 0;
  const inProgressTotal = projectsInProgress + buildsInProgress;

  const projectsCompleted = stats.projects_by_status?.completed ?? 0;
  const buildsCompleted = stats.builds_by_status?.completed ?? 0;
  const completedTotal = projectsCompleted + buildsCompleted;

  const cards: { label: string; value: number; icon: typeof Briefcase; tone: StatTone }[] = [
    {
      label: 'Total Projects',
      value: stats.total_projects,
      icon: Briefcase,
      tone: 'accent',
    },
    {
      label: 'Total Builds',
      value: stats.total_builds,
      icon: Hammer,
      tone: 'info',
    },
    {
      label: 'Team Members',
      value: stats.total_team_members,
      icon: Users,
      tone: 'purple',
    },
    {
      label: 'In Progress',
      value: inProgressTotal,
      icon: Zap,
      tone: 'amber',
    },
    {
      label: 'Completed',
      value: completedTotal,
      icon: CheckCircle,
      tone: 'success',
    },
    {
      label: 'Schematics',
      value: stats.total_schematics,
      icon: FileArchive,
      tone: 'neutral',
    },
    ...(stats.total_project_images != null
      ? [
          {
            label: 'Project images',
            value: stats.total_project_images,
            icon: Image,
            tone: 'neutral' as StatTone,
          },
        ]
      : []),
    {
      label: 'Assignments',
      value: stats.total_active_assignments,
      icon: UserCheck,
      tone: 'accent',
    },
    {
      label: 'Recently Active',
      value: stats.recently_active,
      icon: TrendingUp,
      tone: 'orange',
    },
  ];

  return (
    <div className={GRID}>
      {cards.map((card) => (
        <StatTile
          key={card.label}
          label={card.label}
          value={card.value.toLocaleString()}
          icon={card.icon}
          tone={card.tone}
        />
      ))}
    </div>
  );
};
